import { isUndefined } from 'lodash'
import {
  shallowRef,
  toValue,
  onScopeDispose,
  toRaw,
  toRef,
  provide,
  reactive,
  getCurrentInstance,
  inject
} from 'vue'
import { computed } from 'vue'

import { useToggleScope } from './toggle-scope'

const StackSymbol = Symbol.for('modal:stack')

const globalStack = reactive([])

const Z_INDEX_BASE = 5999

/**
 * Manages stacking context and z-index for modal-like components, supporting both global and local stacking.
 * Ensures correct z-index assignment and tracks top-most status within the stack.
 *
 * @param {import('vue').Ref<boolean>} isActive - Reactive ref indicating if the component is active (visible).
 * @param {Object} [options] - Optional configuration object.
 * @param {import('vue').MaybeRefOrGetter<string | number>} [options.zIndex] - Base z-index value (default: 9999).
 * @param {boolean} [options.disableGlobalStack] - If true, disables global stack management.
 * @returns {{
 *   localTop: import('vue').Ref<boolean>,
 *   stackStyles: import('vue').Ref<{ zIndex: number }>
 * }} Object containing refs for local top status and stack styles.
 */
export function useStack(isActive, options = {}) {
  const { zIndex = Z_INDEX_BASE, disableGlobalStack } = options

  const vm = getCurrentInstance()
  const shouldCreateStackEntry = !disableGlobalStack

  const parent = inject(StackSymbol, undefined)
  const stack = reactive({
    activeChildren: new Set()
  })
  provide(StackSymbol, stack)

  const _zIndex = shallowRef(Number(toValue(zIndex)))
  useToggleScope(isActive, () => {
    const lastItem = globalStack.at(-1)
    const lastZIndex = lastItem?.at(1)

    const normalizedZIndex = Number(toValue(zIndex))
    if (isUndefined(lastZIndex)) {
      _zIndex.value = normalizedZIndex
    } else if (lastZIndex === 0) {
      _zIndex.value = normalizedZIndex === 0 ? Z_INDEX_BASE : normalizedZIndex
    } else {
      _zIndex.value = lastZIndex + 10
    }

    if (shouldCreateStackEntry) {
      globalStack.push([vm.uid, _zIndex.value])
    }

    parent?.activeChildren.add(vm.uid)

    onScopeDispose(() => {
      if (shouldCreateStackEntry) {
        const idx = toRaw(globalStack).findIndex(v => v[0] === vm.uid)
        if (idx !== -1) {
          globalStack.splice(idx, 1)
        }
      }

      parent?.activeChildren.delete(vm.uid)
    })
  })

  // const globalTop = shallowRef(true)
  // if (shouldCreateStackEntry) {
  //   watchEffect(() => {
  //     const lastItem = globalStack.at(-1)
  //     const lastComponentId = lastItem?.at(0)
  //     const _isTop = lastComponentId === vm.uid
  //     setTimeout(() => (globalTop.value = _isTop), 0)
  //   })
  // }

  const localTop = computed(() => !stack.activeChildren.size)

  return {
    localTop,
    stackStyles: toRef(() => ({ zIndex: _zIndex.value }))
  }
}
